import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react';
import ArrowRightAltSharpIcon from '@gamma/ui/icons/ArrowRightAltSharpIcon';
import CalendarTodaySharpIcon from '@gamma/ui/icons/CalendarTodaySharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { CrowdStrikeMark, MsDefenderMark } from '@gamma/ui/logos';
import { RouterLink } from '@gamma/ui/navigation';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { ReactNode, useContext } from 'react';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  DateRangeContext,
  UISettingsContext,
} from '@investigator/shared/providers';
import { useEntitiesContext, useEntityEdrContext } from '../../context';
import { useActiveEntityStates } from '@investigator/features/entities/hooks/useActiveEntityStates';
import { EntityInformationActions } from './components/EntityInformationActions';
import {
  CrowdstrikeModalSection,
  LogscaleModalSection,
  MSDefenderModalSection,
  NotConfiguredSection,
  PaloAltoModalSection,
  UserHistorySection,
} from '../EDRDetailsSections';

const {
  edrNoIntegrationHeadline,
  edrNoIntegrationParagraph,
  browseIntegrations,
  noDataFound,
} = i18n.pages.entityDetails;

const { additionalEdrIntegration } = i18n.pages.integrations;

export const NoDataSection = ({ header }: { header: ReactNode }) => (
  <VStack alignItems="start">
    {header}
    <Text color="gray.400">{noDataFound}</Text>
  </VStack>
);

export const EntityInformationModal = () => {
  const { start, end } = useDateRangeQueryString();

  const { dateRange } = useContext(DateRangeContext);
  const { isIntegrationsBannerDismissed, setIsIntegrationsBannerDismissed } =
    useContext(UISettingsContext);
  const { userPrivileges, isOrgTenant } = useContext(AuthContext);
  const {
    suppressionModal: { onOpen: onSuppressOpen },
    analysisModal: { isOpen, onClose },
    selectedEntity,
  } = useEntitiesContext();

  // Get EDR data from context
  const {
    edrData: organizedData,
    loading,
    error,
    hasAnyEDRIntegrationEnabled,
  } = useEntityEdrContext();

  const { entityName, tenantInfo } = selectedEntity || {};
  const { tenant_id = '' } = tenantInfo || {};

  // Ensure entity state is properly managed (builds excludedEntitiesVariables, etc.)
  useActiveEntityStates();

  const tenantPrivileges = userPrivileges?.[tenant_id || ''];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
      <ModalOverlay />
      <ModalContent w="1280px">
        <ModalHeader>
          <VStack alignItems="start">
            <Text>{selectedEntity?.entityName}</Text>
            <HStack spacing={4} alignItems="start" divider={<StackDivider />}>
              <HStack
                px={2}
                border="1px solid"
                borderRadius="base"
                borderColor="border.1"
              >
                <CalendarTodaySharpIcon size="sm" />

                <Text fontSize="small">
                  {`Last ${dateRange?.[0]} ${dateRange?.[1]}`}
                </Text>
              </HStack>
              <EntityInformationActions
                end={end}
                start={start}
                isModal={true}
                entityName={entityName}
                onClose={onClose}
                onSuppressEntity={onSuppressOpen}
              />
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalBody>
          {!hasAnyEDRIntegrationEnabled && !isIntegrationsBannerDismissed && (
            <HStack
              h="200px"
              border="1px solid"
              borderColor="gray.600"
              backgroundSize="535px"
              backgroundPosition="right"
              backgroundRepeat="no-repeat"
              backgroundImage="/assets/images/ig-evidence-based-security.png"
              marginBottom={2}
              justifyContent="space-between"
            >
              <VStack ml={6} maxW="550px" alignItems="start">
                <Text textStyle="h4">{edrNoIntegrationHeadline}</Text>
                <Text mb={2} textStyle="body-md">
                  {edrNoIntegrationParagraph}
                </Text>
                {!isOrgTenant && (
                  <Button
                    as={RouterLink}
                    variant="solid"
                    colorScheme="blue"
                    to={ROUTES.systemSettingsIntegrationsConfig}
                    onClick={onClose}
                  >
                    {browseIntegrations}
                    &nbsp; &nbsp;
                    <ArrowRightAltSharpIcon />
                  </Button>
                )}
              </VStack>
              <Button
                alignItems="start"
                height="180px"
                onClick={() => setIsIntegrationsBannerDismissed?.(true)}
              >
                <CloseSharpIcon />
              </Button>
            </HStack>
          )}
          {loading ? (
            <GraphQLReqStatus loading={loading} error={error} />
          ) : (
            <VStack alignItems="start" divider={<StackDivider />}>
              <LogscaleModalSection />
              {tenantPrivileges?.crowdstrike &&
                (organizedData?.crowdstrike ? (
                  <CrowdstrikeModalSection />
                ) : organizedData === null ? (
                  <NotConfiguredSection
                    header={
                      <HStack>
                        <CrowdStrikeMark />
                        <Text textStyle="h5">CrowdStrike</Text>
                      </HStack>
                    }
                    message={additionalEdrIntegration}
                    buttonText={browseIntegrations}
                    buttonLink={ROUTES.systemSettingsIntegrationsConfig}
                    hideButton={isOrgTenant}
                    onButtonClick={onClose}
                  />
                ) : (
                  <NoDataSection
                    header={
                      <HStack>
                        <CrowdStrikeMark />
                        <Text textStyle="h5">CrowdStrike</Text>
                      </HStack>
                    }
                  />
                ))}
              {tenantPrivileges?.microsoft_defender &&
                (organizedData?.microsoft_defender ? (
                  <MSDefenderModalSection
                    data={organizedData.microsoft_defender}
                  />
                ) : organizedData === null ? (
                  <NotConfiguredSection
                    header={
                      <HStack>
                        <MsDefenderMark />
                        <Text textStyle="h5">Microsoft Defender</Text>
                      </HStack>
                    }
                    message={additionalEdrIntegration}
                    buttonText={browseIntegrations}
                    buttonLink={ROUTES.systemSettingsIntegrationsConfig}
                    hideButton={isOrgTenant}
                    onButtonClick={onClose}
                  />
                ) : (
                  <NoDataSection
                    header={
                      <HStack>
                        <MsDefenderMark />
                        <Text textStyle="h5">Microsoft Defender</Text>
                      </HStack>
                    }
                  />
                ))}
              {tenantPrivileges?.palo_alto_networks && <PaloAltoModalSection />}
              {(tenantPrivileges?.crowdstrike ||
                tenantPrivileges?.microsoft_entra_id) && (
                <UserHistorySection
                  tenantPrivileges={tenantPrivileges}
                  organizedData={organizedData}
                />
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="border.layer.1">
          <Button variant="solid" colorScheme="gray" onClick={onClose}>
            {i18n.buttons.close}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
